import { computed, inject, signal, Service } from '@angular/core';

import { PlatformService } from '@core/platform/platform.service';
import { StorageService } from '@core/storage/storage.service';
import {
  StorageKeys,
  USER_SCOPED_STORAGE_KEYS,
  USER_SCOPED_SESSION_KEYS,
} from '@core/storage/storage.keys';
import { AuthStatus, AppUser } from '../domain/models/auth.model';

/**
 * AuthSessionStore — the single source of truth for the v2 session (token + user).
 *
 * Root-scoped + signals-first. The token is persisted under the v2-namespaced
 * key (`tlb_v2_auth_token`) via {@link StorageService} — it NEVER touches v1's
 * `PatientTalbinahPortalToken`, honouring the /v2 isolation rule.
 *
 * SSR-safe: hydration is browser-only; on the server status stays `unknown` and
 * the token is `null` (so server-side requests are anonymous, as a gated app
 * should be). The browser re-hydrates from storage on first construction.
 */
@Service()
export class AuthSessionStore {
  private readonly storage = inject(StorageService);
  private readonly platform = inject(PlatformService);

  private readonly _token = signal<string | null>(null);
  private readonly _user = signal<AppUser | null>(null);
  private readonly _status = signal<AuthStatus>('unknown');

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly status = this._status.asReadonly();

  /** `true` only when a token is present AND the status is authenticated. */
  readonly isAuthenticated = computed(() => this._status() === 'authenticated' && !!this._token());

  // ── B2B membership is NOT decided here any more ────────────────────────
  //
  // This store used to carry an `isB2BUser` computed that compared the signed-in
  // user's phone number against one hardcoded pilot account — a deliberate,
  // documented placeholder for a backend signal that did not exist yet.
  //
  // It does now. `GET /api/v3/org/context` (B2B-ORGANIZATIONS-FE-INTEGRATION §2)
  // answers `data.member: null` for a non-member and a populated membership for
  // a member, resolved server-side from `org_members.status` + the organization's
  // own status. §0: "there is no 'is this a B2B user' branch to write" — it is
  // structural, not a flag on the user resource.
  //
  // So the single source of truth is `OrgContextStore.isMember` (root-scoped,
  // `domains/organizations`), and nothing reads it from here. The computed is
  // gone rather than deprecated in place: `core` must not depend on a domain
  // (see core/README.md), so this store genuinely cannot answer the question any
  // more, and leaving a phone-number check beside the real one would be a second,
  // wrong answer waiting to be picked up.

  constructor() {
    this.hydrate();
  }

  /** Read the persisted token/user (browser only). Optimistic — `/me` confirms. */
  hydrate(): void {
    if (!this.platform.isBrowser) {
      this._status.set('unknown');
      return;
    }
    const token = this.storage.get<string>(StorageKeys.AUTH_TOKEN);
    if (token) {
      this._token.set(token);
      this._user.set(this.storage.get<AppUser>(StorageKeys.AUTH_USER));
      this._status.set('authenticated');
    } else {
      this._status.set('guest');
    }
  }

  /** Persist a freshly-minted session (after OTP verify). */
  setSession(token: string, user: AppUser): void {
    this._token.set(token);
    this._user.set(user);
    this._status.set('authenticated');
    this.storage.set(StorageKeys.AUTH_TOKEN, token);
    this.storage.set(StorageKeys.AUTH_USER, user);
  }

  /** Refresh just the cached user (e.g. after a `/me` bootstrap). */
  setUser(user: AppUser): void {
    this._user.set(user);
    this.storage.set(StorageKeys.AUTH_USER, user);
  }

  /**
   * Drop the session token/user (logout / `401`). Status → `guest`.
   *
   * Intentionally MINIMAL — the `401` path must clear only the auth pair (a
   * transient token expiry shouldn't wipe the user's gender voice / subscription
   * snapshot). A DELIBERATE sign-out or account-deletion calls
   * {@link purgeUserScoped} instead to also erase identity-bound data.
   */
  clear(): void {
    this._token.set(null);
    this._user.set(null);
    this._status.set('guest');
    this.storage.remove(StorageKeys.AUTH_TOKEN);
    this.storage.remove(StorageKeys.AUTH_USER);
  }

  /**
   * Full sign-out purge: clear the session AND erase every identity-bound key
   * ({@link USER_SCOPED_STORAGE_KEYS} — gender voice, subscription snapshot,
   * Khoyek reminders). Used by a deliberate logout / account-deletion so that
   * nothing about the departing user survives for the next person on a shared
   * device. Device/app preferences (language · theme · splash · onboarding
   * intro) are intentionally preserved (see the storage-keys doc).
   */
  purgeUserScoped(): void {
    this.clear();
    this.storage.removeMany(USER_SCOPED_STORAGE_KEYS);
    this.storage.removeMany(USER_SCOPED_SESSION_KEYS, /* session */ true);
  }
}
